import { PreconfigInquiry, PreconfigSheetParameter } from '../interfaces/Preconfigurations';
import { DEFAULT_SHEET_PARAMETER_DATA } from '../data/ConstantsPreconfig';
import { LinkedInquiry, Poll, PollFrequency } from '../interfaces/Poll';

export type PreconfigDispatch =
    { type: 'ADD_CONSTANT', sheetParameterID: PreconfigSheetParameter['sheet_parameter_id'], defaultMinLimit?: PreconfigSheetParameter['min_limit'], defaultMaxLimit?: PreconfigSheetParameter['max_limit'] } |
    { type: 'DELETE_CONSTANT', sheetParameterID: PreconfigSheetParameter['sheet_parameter_id'] } |
    { type: 'CHANGE_MIN_LIMIT', sheetParameterID: PreconfigSheetParameter['sheet_parameter_id'], value: PreconfigSheetParameter['min_limit'] } |
    { type: 'CHANGE_MAX_LIMIT', sheetParameterID: PreconfigSheetParameter['sheet_parameter_id'], value: PreconfigSheetParameter['max_limit'] } |
    { type: 'CHANGE_REMINDER_TIME', sheetParameterID: PreconfigSheetParameter['sheet_parameter_id'], value: PreconfigSheetParameter['reminder_time'] } |
    { type: 'CHANGE_FREQ', sheetParameterID: PreconfigSheetParameter['sheet_parameter_id'], value: PollFrequency } |
    { type: 'SET_DEFAULT', sheetParameters: PreconfigSheetParameter[] }

export const patientPreconfigReducer = (state: PreconfigSheetParameter[], action: PreconfigDispatch): PreconfigSheetParameter[] => {

    if (action.type === 'ADD_CONSTANT') {
        return [...state, {
            ...DEFAULT_SHEET_PARAMETER_DATA,
            sheet_parameter_id: action.sheetParameterID,
            min_limit: action.defaultMinLimit,
            max_limit: action.defaultMaxLimit
        }];
    }

    if (action.type === 'DELETE_CONSTANT')
        return state.filter(sp => sp.sheet_parameter_id !== action.sheetParameterID);

    if (action.type === 'CHANGE_MIN_LIMIT') return state.map(p => {
        if (p.sheet_parameter_id !== action.sheetParameterID) return p;
        return { ...p, min_limit: action.value };
    });

    if (action.type === 'CHANGE_MAX_LIMIT') return state.map(p => {
        if (p.sheet_parameter_id !== action.sheetParameterID) return p;
        return { ...p, max_limit: action.value };
    });

    if (action.type === 'CHANGE_REMINDER_TIME') return state.map(p => {
        if (p.sheet_parameter_id !== action.sheetParameterID) return p;
        return { ...p, reminder_time: action.value };
    });

    if (action.type === 'CHANGE_FREQ') return state.map(p => {
        if (p.sheet_parameter_id !== action.sheetParameterID) return p;
        return { ...p, ...action.value };
    });

    if (action.type === 'SET_DEFAULT') return action.sheetParameters;

    return state;
};

export type PreconfigInquiryDispatch =
    { type: 'ADD_INQUIRY', poll: Poll | PreconfigInquiry } |
    { type: 'CHANGE_INQUIRY', oldID: number, poll: Poll | PreconfigInquiry } |
    { type: 'DELETE_INQUIRY', id: number } |
    { type: 'CHANGE_FREQUENCY', id: number, freq: PollFrequency } |
    { type: 'CHANGE_REMINDER', id: number, reminder: PreconfigInquiry['reminder_time'] } |
    { type: 'SET_DEFAULT', inquiries: PreconfigInquiry[] }


const setPoll = (poll: Poll | PreconfigInquiry) => {
    return {
        inquiry_id: poll.id ?? -1,
        frecuency: poll.frecuency,
        semanal_frequency: poll.semanal_frequency,
        monthly_frequency_day: poll.monthly_frequency_day,
        frequency_duration: poll.frequency_duration,
        reminder_time: poll.reminder_time
    };
};

export const preconfigInquiriesReducer = (state: PreconfigInquiry[], action: PreconfigInquiryDispatch): PreconfigInquiry[] => {

    if (action.type === 'ADD_INQUIRY' && typeof action.poll.id === 'number')
        return [...state, setPoll(action.poll)];
    if (action.type === 'CHANGE_INQUIRY') return state.map(i => {
        if (i.inquiry_id !== action.oldID) return i;
        return setPoll(action.poll);
    });
    if (action.type === 'DELETE_INQUIRY') return state.filter(i => i.inquiry_id !== action.id);
    if (action.type === 'CHANGE_FREQUENCY') return state.map(pi => {
        if (pi.inquiry_id !== action.id) return pi;
        return { ...pi, ...action.freq };
    });
    if (action.type === 'CHANGE_REMINDER') return state.map(pi => {
        if (pi.inquiry_id !== action.id) return pi;
        return { ...pi, reminder_time: action.reminder };
    });
    if (action.type === 'SET_DEFAULT') return action.inquiries;
    return state;
};

export type PreconfigPatientInquiryDispatch =
    { type: 'ADD_INQUIRY', inquiryID: number, poll: Poll | PreconfigInquiry, patient_id: number } |
    { type: 'CHANGE_INQUIRY', oldID: number, inquiryID: number, poll: Poll | PreconfigInquiry, patient_id: number } |
    { type: 'DELETE_INQUIRY', id: number } |
    { type: 'CHANGE_FREQUENCY', id: number, freq: PollFrequency } |
    { type: 'CHANGE_REMINDER', id: number, reminder: PreconfigInquiry['reminder_time'] } |
    { type: 'SET_DEFAULT', inquiries: PreconfigInquiry[] }

type PostPatientInquiry = Omit<
    LinkedInquiry, 'inquiry' | 'modified_frequency' | 'modified_reminder' | 'start_date'
>

export const preconfigPatientInquiriesReducer = (state: PostPatientInquiry[], action: PreconfigPatientInquiryDispatch): PostPatientInquiry[] => {

    if (action.type === 'ADD_INQUIRY' && typeof action.poll.id === 'number')
        return [...state, {
            patient_id: action.patient_id,
            end_date: null,
            ...setPoll(action.poll),
            inquiry_id: action.inquiryID,
        }];
    if (action.type === 'CHANGE_INQUIRY') return state.map(i => {
        if (i.inquiry_id !== action.oldID) return i;
        return {
            patient_id: action.patient_id,
            end_date: null,
            ...setPoll(action.poll),
            inquiry_id: action.inquiryID,
        };
    });
    if (action.type === 'DELETE_INQUIRY') return state.filter(i => i.inquiry_id !== action.id);
    if (action.type === 'CHANGE_FREQUENCY') return state.map(pi => {
        if (pi.inquiry_id !== action.id) return pi;
        return { ...pi, ...action.freq, modifed_frequency_by_preconfig: true };
    });
    if (action.type === 'CHANGE_REMINDER') return state.map(pi => {
        if (pi.inquiry_id !== action.id) return pi;
        return { ...pi, reminder_time: action.reminder, modifed_reminder_by_preconfig: true };
    });
    if (action.type === 'SET_DEFAULT') return action.inquiries.map(i => {
        return {
            ...setPoll(i),
            inquiry_id: i.inquiry_id,
            patient_id: i.patient_id ?? -1
        };
    });
    return state;
};

